import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import type { CollectionView } from "../../../shared/milestones";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n } from "../../i18n/I18nProvider";
import { api } from "../../lib/api";
import { EvolutionModal } from "./EvolutionModal";
import { StarterPicker } from "./StarterPicker";

interface CollectionValue {
  collection: CollectionView | null;
  /** Re-reads it (new milestones are recorded on the way). */
  reload: () => Promise<void>;
  /** After a change made elsewhere (starter, avatar choice). */
  update: (c: CollectionView) => void;
}

const Ctx = createContext<CollectionValue>({ collection: null, reload: async () => {}, update: () => {} });
export const useCardCollection = () => useContext(Ctx);

/** Re-checked on navigation, at most this often: a purchase or a new alert shows up soon after. */
const RECHECK_MS = 20_000;
const STARTER_PROMPT_KEY = "pf-starter-prompted";

/**
 * Card avatar of the signed-in customer, shared by Mi colección, plus what the whole site shows:
 * the starter choice (once per visit, until they pick) and the evolution animation on a level up.
 */
export function CardCollectionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { t } = useI18n();
  const { pathname } = useLocation();
  const userId = auth.user?.id ?? null;
  const [collection, setCollection] = useState<CollectionView | null>(null);
  const [prompt, setPrompt] = useState(false);
  const last = useRef(0);

  const apply = useCallback(
    (c: CollectionView) => {
      setCollection(c);
      // The header avatar comes from the session: refresh it when the card changed.
      if (c.avatarUrl !== (auth.user?.image ?? null)) void auth.refresh();
    },
    [auth],
  );

  const reload = useCallback(async () => {
    if (!userId) return;
    last.current = Date.now();
    try {
      apply(await api.collection());
    } catch {
      // Offline or signed out meanwhile: keep what we had.
    }
  }, [userId, apply]);

  useEffect(() => {
    if (!userId) return setCollection(null);
    if (Date.now() - last.current >= RECHECK_MS || !collection) void reload();
  }, [userId, pathname]);

  useEffect(() => {
    if (!collection || collection.starter || pathname.startsWith("/cuenta") || pathname.startsWith("/checkout")) return;
    try {
      if (sessionStorage.getItem(STARTER_PROMPT_KEY)) return;
      sessionStorage.setItem(STARTER_PROMPT_KEY, "1");
    } catch {
      // No storage: still ask once for this page view.
    }
    setPrompt(true);
  }, [collection, pathname]);

  const evolving = collection?.starter && collection.level > collection.seenLevel ? collection : null;
  const closeEvolution = useCallback(() => {
    if (!collection) return;
    setCollection({ ...collection, seenLevel: collection.level });
    void api.collectionSeen(collection.level).catch(() => {});
  }, [collection]);

  return (
    <Ctx.Provider value={{ collection, reload, update: apply }}>
      {children}
      {prompt && collection && !collection.starter && (
        <div className="evo-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setPrompt(false)}>
          <div className="evo-modal starter-modal" role="dialog" aria-modal="true" aria-labelledby="starter-title">
            <h2 id="starter-title">{t("cards.chooseTitle")}</h2>
            <p className="muted">{t("cards.chooseIntro")}</p>
            <StarterPicker
              onPick={async (key) => {
                apply(await api.chooseStarter(key));
                setPrompt(false);
              }}
            />
            <button type="button" className="btn btn-link" onClick={() => setPrompt(false)}>
              {t("cards.later")}
            </button>
          </div>
        </div>
      )}
      {evolving && !prompt && <EvolutionModal collection={evolving} from={Math.max(1, evolving.seenLevel)} onClose={closeEvolution} />}
    </Ctx.Provider>
  );
}
